import React, { ReactElement, useState } from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import { useCommon } from 'src/AppState/Common';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { BonusCreationStore } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreation.store';
import { BONUS_CREATOR_ROOT } from 'src/domains/casino/utils/test-variables';
import { BonusCreationsTable } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creations-table/BonusCreationsTable';
import { BonusCreatorState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/BonusCreator.state';
import { Button } from 'src/domains/common/components/button/button';
import { observer } from 'src/utils/mobx-react';

export const BonusCreator = observer('BonusCreator', (): ReactElement | null => {
    const { routerBackoffice } = useAppStateContext();
    const common = useCommon();
    const [bonusCreatorState] = useState(() => new BonusCreatorState(common, routerBackoffice));
    const bonusCreationStore = BonusCreationStore.get(common);

    if (bonusCreationStore.isLoading === true) {
        return (
            <LoadingV2
                dataTest={BONUS_CREATOR_ROOT}
                title='Loading bonus creations...'
            />
        );
    }

    return (
        <div
            className='flex flex-col flex-auto max-h-screen gap-6 p-6 bg-white font-tertiary flex-nowrap'
            data-test={BONUS_CREATOR_ROOT}
        >
            <header className='flex items-center justify-between flex-[0_0_36px]'>
                <div className='flex flex-col gap-2'>
                    <h2
                        className='text-foreground m-0 text-[1rem] font-semibold leading-[1.75]'
                        data-test='page-title'
                    >
                        Bonus List
                    </h2>

                    <p
                        className='text-muted-foreground m-0 text-sm text-pretty max-w-2xl'
                        data-test='page-desc'
                    >
                        Create and manage bonuses, these just define the reward conditions. These bonuses can then be
                        credited via third party integrations. Such as via a CRM.
                    </p>
                </div>
                <Button
                    icon='plus'
                    onClick={() =>
                        routerBackoffice.redirectToBonusCreation({
                            type: 'NEW',
                            view: 'general-details',
                            cloneId: null,
                        })
                    }
                    size='default'
                    variant='primary'
                    dataTest='new-bonus-creation-button'
                    className='self-start'
                >
                    New Bonus
                </Button>
            </header>

            {bonusCreationStore.hasZeroBonusCreations === true ? (
                <article className='flex flex-col items-center justify-center flex-1 gap-4 text-center'>
                    <h3 className='m-0 text-sm text-foreground'>Start Your First Bonus Creation</h3>
                    <p className='m-0 text-xs text-muted-foreground'>
                        It looks like you haven&#39;t created any bonus rewards yet.
                        <br />
                        To get started, click the &lsquo;New Bonus&rsquo; button in the top right corner.
                        <br />
                        Set up your first bonus reward and it will appear here.
                    </p>
                </article>
            ) : (
                <BonusCreationsTable bonusCreatorState={bonusCreatorState} />
            )}
        </div>
    );
});
